import { Domain, User, UserRole, Instruction } from "@lr-notitia/notitia-engineer/schemas";
import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import { LanguageCode } from "iso-639-1";
// import { H } from "highlight.run";
import { Dependency } from "@lr-notitia/notitia-model-store/schemas";
import { useDependenciesStore } from "./dependencies";
import * as Metrics from "./metrics";

let dependenciesStore: ReturnType<typeof useDependenciesStore>;
function init() {
	dependenciesStore = useDependenciesStore();
}

export const useDomainStore = defineStore("domain", () => {
	let domainId = "";
	const notFound = ref(true);
	const domain = ref<Domain>({} as Domain);
	const account = ref<User>({} as User);
	const members = ref<User[]>([]);
	const languages = ref<LanguageCode[]>([]);
	const languagePreference = ref<LanguageCode>(
		getLocalStorageLanguage() && getLocalStorageLanguage() !== ""
			? (getLocalStorageLanguage() as LanguageCode)
			: ((navigator.language.split("-")[0] || "en") as LanguageCode)
	);
	const allDomains = ref<Domain[]>([]);

	const isAdmin = computed(() => account.value.role === UserRole.Admin);
	const isEditorOrAdmin = computed(() => account.value.role === UserRole.Editor || isAdmin.value);

	const loadingDuplicate = ref(false);

	function loadDomain(dId: string): Promise<Domain> {
		domainId = dId;
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.get()
				.then(async (data) => {
					domain.value = data;
					languages.value = data.languages;
					notFound.value = false;
					await Metrics.createRecording(dId).catch(() => console.warn("Failed to create recording"));
					loadAccount(dId)
						.then(() => loadMembers(dId))
						.then(() => resolve(data));
				})
				.catch(() => reject());
		});
	}
	function clearDomain() {
		notFound.value = true;
		domain.value = {} as Domain;
		account.value = {} as User;
		members.value = [];
		domainId = "";
	}
	function loadAllDomains(): Promise<Domain[]> {
		return new Promise((resolve) => {
			api()
				.domains()
				.get()
				.then((data) => {
					allDomains.value = data;
					resolve(data);
				});
		});
	}

	function loadAccount(dId: string): Promise<void> {
		return new Promise((resolve) => {
			api()
				.domain(dId)
				.account()
				.get()
				.then((data) => {
					account.value = data;
					resolve();
					// if (import.meta.env.MODE != "development") {
					// 	H.identify(data.email, {
					// 		id: data.id,
					// 		role: data.role,
					// 	});
					// }
				})
				.catch(() => console.warn("Failed to load Account"));
		});
	}
	function loadMembers(dId: string): Promise<void> {
		return new Promise((resolve) => {
			api()
				.domain(dId)
				.members()
				.get()
				.then((data) => {
					members.value = data;
					resolve();
				})
				.catch(() => console.warn("Failed to load Members"));
		});
	}

	function searchMember(query: string): Promise<User[]> {
		return new Promise((resolve) => {
			api()
				.domain(domainId)
				.searchUsers()
				.get(query)
				.then((data) => resolve(data))
				.catch(() => resolve([]));
		});
	}
	function addMember(user: User, role: UserRole) {
		api()
			.domain(domainId)
			.members()
			.post({ userId: user.id, role })
			.then((d) => (members.value = d))
			.catch(() => console.warn("Failed to add member"));
	}
	function moveMember(user: User, role: UserRole) {
		if (!canModify(role)) return;
		api()
			.domain(domainId)
			.members()
			.del({ userId: user.id })
			.then(() => {
				api()
					.domain(domainId)
					.members()
					.post({ userId: user.id, role })
					.then((d) => (members.value = d))
					.catch(() => console.warn("Failed to move member"));
			})
			.catch(() => console.warn("Failed to move member"));
		return;
	}
	function removeMember(userId: string) {
		api()
			.domain(domainId)
			.members()
			.del({ userId })
			.then(() => {
				members.value = members.value.filter((m) => m.id !== userId);
			})
			.catch(() => console.warn("Failed to remove member"));
	}

	function canModify(targetUserRole: UserRole) {
		let modifyableRoles: string[] = [];
		switch (account.value.role) {
			case UserRole.Admin:
				modifyableRoles = [UserRole.Admin, UserRole.Editor, UserRole.Viewer];
				break;
			case UserRole.Editor:
				modifyableRoles = [UserRole.Viewer];
				break;
			case UserRole.Viewer:
				modifyableRoles = [];
				break;
		}
		if (modifyableRoles.includes(targetUserRole)) return true;
		else return false;
	}

	function editDomain(domId: string, name: string, dependencies: Dependency[], languages: LanguageCode[] = []) {
		api()
			.domain(domId)
			.put({ name, languages, dependencies })
			.then((data: Domain) => {
				domain.value = data;
			});
	}
	function releaseDomain(domId: string, instruction: Instruction): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domId)
				.release({ instruction })
				.then(() => {
					resolve();
					loadAllDomains();
				})
				.catch(() => {
					reject();
					console.warn("Failed to release domain");
				});
		});
	}
	function hasAccessRights() {
		return account.value.role != "viewer";
	}

	function getLocalStorageLanguage(): string {
		return localStorage.getItem("languagePreference") || "";
	}
	function setLocalStorageLanguage(lang: LanguageCode) {
		localStorage.setItem("languagePreference", lang);
	}

	function domainColor(domainId: string): string {
		if (!domainId) return "#ffffff";
		const colors = [
			// these colors are too light to be readable in light mode
			// "#b2dfdb",
			// "#f0f4c3",
			// "#c8e6c9",
			// "#ffcdd2",
			// "#f8bbd0",
			// "#ffe0b2",
			// "#b3e5fc",
			// "#d1c4e9",
			// "#4db6ac",
			// "#dce775",
			// "#81c784",
			// "#e57373",
			// "#f06292",
			// "#ffb74d",
			"#4fc3f7",
			"#9575cd",
			"#00796b",
			"#afb42b",
			"#388e3c",
			"#d32f2f",
			"#c2185b",
			"#f57c00",
			// these might be too dark for dark mode
			// "#1486c9",
			// "#4c3b8f",
		];
		const hash = domainId.split("").reduce((a, b) => {
			a = (a << 5) - a + b.charCodeAt(0);
			return a & a;
		}, 0);
		return colors[Math.abs(hash) % colors.length];
	}

	function loadDependencies(): Promise<void> {
		return new Promise((resolve) => {
			if (!dependenciesStore) init();
			if (!domain.value.id) {
				dependenciesStore.clearGraphs();
				resolve();
			} else {
				const dependencies = getAllDependenciesIds(domain.value.id);
				dependenciesStore.loadGraphs(dependencies).then(() => resolve());
			}
		});
	}
	function getAllDependenciesIds(domainId: string): Dependency[] {
		const dom = allDomains.value.find((d) => d.id === domainId);
		if (!dom) return [];
		let dependencies: Dependency[] = [];
		dom.dependencies.forEach((dep) => {
			dependencies.push(dep);
			dependencies = dependencies.concat(getAllDependenciesIds(dep.domainId));
		});
		dependencies = dependencies.filter(
			(dep, index, self) => self.findIndex((d) => d.domainId === dep.domainId) === index
		);
		return dependencies;
	}

	watch(
		() => members.value,
		() => {
			members.value.sort((a, b) => {
				if (a.lastname < b.lastname) return -1;
				if (a.lastname > b.lastname) return 1;
				return 0;
			});
		}
	);
	watch(
		() => allDomains.value,
		() => {
			allDomains.value.sort((a, b) => {
				if (a.name < b.name) return -1;
				if (a.name > b.name) return 1;
				return 0;
			});
		}
	);
	watch(
		() => languagePreference.value,
		(newVal) => setLocalStorageLanguage(newVal)
	);

	return {
		isAdmin,
		isEditorOrAdmin,
		hasAccessRights,
		notFound,
		domain,
		editDomain,
		account,
		members,
		languages,
		languagePreference,
		loadDomain,
		clearDomain,
		loadAccount,
		loadMembers,
		searchMember,
		addMember,
		moveMember,
		removeMember,
		canModify,
		domainColor,
		allDomains,
		releaseDomain,
		loadAllDomains,
		loadDependencies,
		loadingDuplicate,
	};
});
